import { Subscribe } from '@react-rxjs/core'
import React, { useEffect, useMemo } from 'react'

import { CartFragment, GetCustomerCartQuery } from '../../graphql/magento'
import useRetailPrices from '../../hooks/useRetailPrices'
import {
  latestCartItemProducts$,
  setCartItemSku,
  useCartItemProducts,
} from '../../lib/cart/state/cart-items/cart-item-product'
import {
  cartSubtotal,
  formatAsCurrency,
  formatAsCurrencyOrZero,
  salesTax,
  shippingTotal,
  taxType,
} from '../../utils/priceTools'
import CartAppliedCoupons from './CartAppliedCoupons'
import CartAppliedRebates from './CartAppliedRebates'
import CartRetailDeliveryFee from './CartRetailDeliveryFee'
import CartSummaryRow from './CartSummaryRow'

export interface CartSummaryProps {
  className?: string
  cart: NonNullable<GetCustomerCartQuery['customerCart']>
  summaryVisible: boolean
}
export const cartTotal = ({ cart }: { cart: CartFragment }): string =>
  formatAsCurrencyOrZero({ amount: cart.prices?.grand_total?.value || 0 })

const RetailSubtotal = ({ cart, skus }: { cart: CartFragment; skus: string[] }) => {
  const { shouldDisplayRetailPrice } = useRetailPrices()
  const products = useCartItemProducts(skus)

  const retailSubtotal = useMemo(() => {
    if (!products || products.length === 0) {
      return 0
    }
    let total = 0
    cart.items?.map(
      (item, index) =>
        (total +=
          parseFloat(
            products[index]?.retailPrice?.amount ?? (products[index]?.price?.amount || '0'),
          ) * (item?.quantity ?? 0)),
    )

    return total
  }, [cart.items, products])

  // Only render if retail prices should be shown
  if (!shouldDisplayRetailPrice || skus.length === 0) {
    return null
  }

  return (
    <div className="flex justify-between">
      <div>Retail Subtotal:</div>
      <div>{formatAsCurrency({ amount: retailSubtotal })}</div>
    </div>
  )
}

const CartSummary: React.FC<CartSummaryProps> = ({ className, cart, summaryVisible }) => {
  const { prices, items } = cart
  const skus = useMemo(
    () => cart.items?.map((item) => item?.product.sku || '').filter(Boolean) || [],
    [cart.items],
  )

  useEffect(() => {
    cart.items?.forEach((item) => {
      if (item?.product.sku) {
        setCartItemSku(item.product.sku)
      }
    })
  }, [cart.items])

  if (!summaryVisible || !cart) {
    return null
  }

  return (
    <div className={className}>
      <Subscribe source$={latestCartItemProducts$(skus)} fallback={null}>
        <RetailSubtotal cart={cart} skus={skus} />
      </Subscribe>
      <CartSummaryRow>
        <div>Subtotal:</div>
        <div>{cartSubtotal({ items, prices })}</div>
      </CartSummaryRow>
      <CartSummaryRow>
        <div>Shipping:</div>
        <div>{shippingTotal({ shippingAddresses: cart.shipping_addresses, items })}</div>
      </CartSummaryRow>
      <CartSummaryRow>
        <div>{`Sales Tax${taxType({ prices })}:`}</div>
        <div>{salesTax({ prices })}</div>
      </CartSummaryRow>
      <CartRetailDeliveryFee cart={cart} />
      <CartAppliedCoupons cart={cart} />
      <CartAppliedRebates cart={cart} />
      <div className="flex justify-between text-sm font-bold">
        <div>Estimated Total:</div>
        <p>{cartTotal({ cart })}</p>
      </div>
    </div>
  )
}

export default CartSummary
